/**
 * tableServerPrintListener.js
 *
 * Main Electron renderer consumer for kitchen/bar order print requests forwarded
 * from the local table-server HTTP route:
 *
 *   Non-Main tablet → POST /tables/printOrders
 *     → Electron main: table-server:print-orders IPC
 *     → preload: window.tableServer.onPrintOrders(cb)
 *     → this module → OrdersService.printOrder (Printer1–N on Main)
 *
 * SPEC / PLAN:
 *   - docs/design-docs/2026-07-24-25724-tablet-order-slips-SPEC.md (AD-2, AD-6, AD-8)
 *   - docs/design-docs/2026-07-24-25724-tablet-order-slips-PLAN.md (TASK 002)
 *   - docs/DESIGN.md § multi-device Approach A
 *
 * Register once at app bootstrap (TASK 005 / main.js). No-op outside Electron Main
 * (when window.tableServer.onPrintOrders is missing). Print failures are non-fatal.
 */

import OrdersService from '@/services/OrdersService';

/** @type {boolean} */
let registered = false;

/** @type {null | (() => void)} Unsubscribe returned by preload onPrintOrders */
let unsubscribe = null;

/**
 * Normalize a table-server printOrders body into a sale-lines array for OrdersService.printOrder.
 *
 * Accepts (best-effort, in priority order):
 * - payload.items (array) — preferred AD-2 LAN shape from table-module
 * - payload.lines (array)
 * - payload.log (array of lines)
 * - payload.log (object with items array)
 * - payload.CookingIsnt / payload.cookingIsnt (JSON string or array of kitchen items)
 *
 * @param {unknown} payload
 * @returns {Array<object>}
 */
function normalizePrintLines(payload) {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const p = /** @type {Record<string, unknown>} */ (payload);

  if (Array.isArray(p.items) && p.items.length > 0) {
    return p.items;
  }
  if (Array.isArray(p.lines) && p.lines.length > 0) {
    return p.lines;
  }

  if (Array.isArray(p.log) && p.log.length > 0) {
    return p.log;
  }
  if (p.log && typeof p.log === 'object' && !Array.isArray(p.log)) {
    const logObj = /** @type {Record<string, unknown>} */ (p.log);
    if (Array.isArray(logObj.items) && logObj.items.length > 0) {
      return logObj.items;
    }
    if (Array.isArray(logObj.lines) && logObj.lines.length > 0) {
      return logObj.lines;
    }
  }

  // Best-effort kitchenlog / online-order CookingIsnt field (typo retained for API compat)
  const cookingRaw = p.CookingIsnt ?? p.cookingIsnt;
  if (typeof cookingRaw === 'string' && cookingRaw.trim()) {
    try {
      const parsed = JSON.parse(cookingRaw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (_) {
      // leave empty; caller will warn
    }
  }
  if (Array.isArray(cookingRaw) && cookingRaw.length > 0) {
    return cookingRaw;
  }

  // Empty arrays on preferred keys still count as "no lines"
  if (Array.isArray(p.items)) return p.items;
  if (Array.isArray(p.lines)) return p.lines;
  if (Array.isArray(p.log)) return p.log;

  return [];
}

/**
 * Handle one print-orders IPC payload: normalize lines, print on Main, never throw to UI.
 *
 * @param {unknown} payload - Body from POST /tables/printOrders (forwarded via IPC)
 * @returns {Promise<void>}
 */
async function handlePrintOrdersPayload(payload) {
  try {
    console.log('[tableServerPrintListener] received print-orders', {
      hasItems: Array.isArray(/** @type {any} */ (payload)?.items),
      hasLines: Array.isArray(/** @type {any} */ (payload)?.lines),
      hasLog: !!(/** @type {any} */ (payload)?.log),
      tableName: /** @type {any} */ (payload)?.tableName,
      saleNumber: /** @type {any} */ (payload)?.saleNumber ?? /** @type {any} */ (payload)?.SaleNum
    });

    const lines = normalizePrintLines(payload);
    if (!Array.isArray(lines) || lines.length === 0) {
      console.warn('[tableServerPrintListener] empty lines after normalize — skip print', payload);
      return;
    }

    const p = /** @type {Record<string, any>} */ (payload && typeof payload === 'object' ? payload : {});
    const saleNumber = p.saleNumber ?? p.SaleNum;
    const options = {
      tableName: p.tableName,
      saleNumber,
      cashier: p.cashier,
      orderNumber: p.orderNumber ?? p.saleNumber ?? saleNumber,
      separators: p.separators !== false
    };

    const result = await OrdersService.printOrder(lines, options);
    console.log('[tableServerPrintListener] printOrder result', result);
  } catch (err) {
    // AD-8: print failure non-fatal; never throw into UI / IPC callback
    console.warn('[tableServerPrintListener] print failed (non-fatal):', err);
  }
}

/**
 * Register once for table-server:print-orders via window.tableServer.onPrintOrders.
 * Safe to call multiple times (module-level guard). No-op when not Electron Main / preload missing.
 *
 * @returns {boolean} true if a new subscription was installed
 */
export function registerTableServerPrintListener() {
  if (registered) {
    return false;
  }

  const onPrintOrders = typeof window !== 'undefined'
    ? window.tableServer?.onPrintOrders
    : undefined;

  if (typeof onPrintOrders !== 'function') {
    // Browser, Android Capacitor, or preload without TASK 001 bridge — intentional no-op (AD-6)
    console.log('[tableServerPrintListener] skip register: window.tableServer.onPrintOrders not available');
    return false;
  }

  try {
    unsubscribe = onPrintOrders((payload) => {
      // Fire-and-forget; errors handled inside handlePrintOrdersPayload
      void handlePrintOrdersPayload(payload);
    });
    registered = true;
    console.log('[tableServerPrintListener] registered for table-server:print-orders');
    return true;
  } catch (err) {
    console.warn('[tableServerPrintListener] register failed (non-fatal):', err);
    registered = false;
    unsubscribe = null;
    return false;
  }
}

/**
 * Remove the IPC subscription if one was installed. Safe for HMR / tests.
 *
 * @returns {void}
 */
export function unregisterTableServerPrintListener() {
  try {
    if (typeof unsubscribe === 'function') {
      unsubscribe();
    }
  } catch (err) {
    console.warn('[tableServerPrintListener] unregister failed (non-fatal):', err);
  } finally {
    unsubscribe = null;
    registered = false;
  }
}

export default {
  registerTableServerPrintListener,
  unregisterTableServerPrintListener
};
